'use client'

import { Fragment } from 'react'
import { Popover, Transition } from '@headlessui/react'
import { MapPinIcon, LinkIcon } from '@heroicons/react/24/outline'
import parse from 'html-react-parser'

import { dateToString } from 'utils/date'
import { capitalizeString } from 'utils/strings'
import { useTorContext } from 'utils/context'

function formatFieldValues(
  items = [],
  { getLabel, getValue, formatLabel = (s) => s, delimiter = ' / ' }
) {
  if (!items.length) return ''

  const values = items.map(getValue).map(String)
  const first = values[0]

  if (values.every((v) => v === first)) {
    return first
  }

  return items
    .map((item) => `${formatLabel(getLabel(item))}: ${getValue(item)}`)
    .join(delimiter)
}

function buildGoogleMapsLink(latitude, longitude, zoom = 15) {
  const lat = encodeURIComponent(latitude)
  const lng = encodeURIComponent(longitude)
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}&zoom=${zoom}`
}

const Info = () => {
  const { tor } = useTorContext()
  let rows = [
    tor.Info.MaximumDate && {
      title: 'Data límit d’inscripció',
      content: dateToString(tor.Info.MaximumDate).full,
      // cta: 'Inscrits',
      // link: '#',
      icon: 'calendar-white.svg'
    },
    // {
    //   title: 'Categoria del Torneig',
    //   content: '{Categoria}',
    //   icon: 'badge-white.svg'
    // },
    tor.Info.Director && {
      title: 'Director/a del Torneig',
      content: tor.Info.Director,
      icon: 'star-white.svg'
    },
    tor.Info.Judge && {
      title: 'Equip arbitral',
      content: tor.Info.Judge.replaceAll('/', ' / '),
      icon: 'referee-white.svg'
    },
    tor.Categories?.length && {
      title: 'Categories de joc',
      content: tor.Categories?.map(({ Modality }) =>
        capitalizeString(Modality)
      ).join(' / '),
      icon: 'badge-white.svg'
    },
    tor.Categories?.length && {
      title: 'Preu d’inscripció',
      content: formatFieldValues(tor.Categories, {
        getLabel: (c) => c.Modality,
        getValue: (c) => c.RegistrationPrice,
        formatLabel: capitalizeString,
        delimiter: ' / '
      }),
      icon: 'money-white.svg'
    },
    tor.Categories?.length && {
      title: 'Ubicació del club',
      content: formatFieldValues(tor.Categories, {
        getLabel: (c) => c.Modality,
        getValue: (c) => `${c.Address}, ${c.Population}`,
        formatLabel: capitalizeString,
        delimiter: ' / '
      }),
      iconComponent: (
        <MapPinIcon className="h-5 w-5 md:h-6 md:w-6 lg:h-8 lg:w-8 text-white" />
      ),
      cta: <LinkIcon className="h-5 w-5 md:h-6 md:w-6 lg:h-8 lg:w-8 ml-4" />,
      link:
        tor.Categories[0]?.Latitude && tor.Categories[0]?.Longitude
          ? buildGoogleMapsLink(
              tor.Categories[0].Latitude,
              tor.Categories[0].Longitude
            )
          : '#'
    },
    tor.AdditionalInformation?.Detail && {
      title: 'Informació Addicional',
      dropdown: true,
      content: parse(tor.AdditionalInformation?.Detail || ''),
      icon: 'money-white.svg'
    }
  ]

  rows = rows.filter((o) => o)

  const download = {
    content: 'Cartell del Torneig',
    link: tor.AdditionalInformation.Image1
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 gap-y-4 lg:gap-y-2">
        {rows.length &&
          rows.map((row, idx) => (
            <div key={idx}>
              <div
                className={`grid grid-cols-1 lg:grid-cols-3 min-w-full ${
                  row?.dropdown ? 'hidden' : null
                }`}
              >
                <div className="bg-blue whitespace-nowrap py-0 lg:py-4 pl-4 pr-3 sm:pl-6 rounded-t-md lg:rounded-tb-none lg:rounded-tr-none lg:rounded-l-md">
                  <div className="flex items-center">
                    <>
                      {row?.icon ? (
                        <img
                          src={`/assets/icons/${row?.icon}`}
                          alt="icon"
                          className="h-5 w-5 md:h-6 md:w-6 lg:h-8 lg:w-8"
                        />
                      ) : (
                        row?.iconComponent
                      )}
                    </>
                    <div className="ml-4 mr-16 py-5">
                      <div className="text-white text-[18px] lg:text-[25px]">
                        {row.title}
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className={`grid col-span-2 px-6 py-4 bg-sec-grey-3 content-center lg:rounded-bl-none lg:rounded-r-md`}
                >
                  <div className="flex items-center text-blue font-alliance-medium text-[18px] lg:text-[25px]">
                    {row.content}
                    {row.cta && (
                      <a
                        href={row.link}
                        target="_blank"
                        className="ml-auto mr-2 lg:mr-4"
                      >
                        {row.cta}
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <div className={row?.dropdown ? null : 'hidden'}>
                <Popover className="">
                  <Popover.Button className="outline-none w-full group">
                    {({ open }) => {
                      return (
                        <div
                          className={`group grid grid-cols-3 min-w-full text-white bg-blue hover:text-blue hover:bg-sec-grey-4 ${
                            open ? 'rounded-t-md' : 'rounded-md'
                          }`}
                        >
                          <div className="whitespace-nowrap py-0 lg:py-4 pl-4 pr-3 sm:pl-6">
                            <div className="flex items-center">
                              {row?.icon ? (
                                <row.icon
                                  className="h-10 w-10"
                                  aria-hidden="true"
                                />
                              ) : null}
                              <div className="ml-4 mr-32 py-5">
                                <div className="text-[18px] lg:text-[25px] group-hover:font-alliance-medium">
                                  {row.title}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            className={`grid col-span-2 font-alliance-medium content-center justify-end pr-10 ${
                              open ? 'rounded-tr-md' : 'rounded-r-md'
                            }`}
                          >
                            {open ? (
                              <>
                                <img
                                  src={`/assets/icons/arrow-simple-down.svg`}
                                  alt="icon"
                                  className="transition rotate-180 w-6 lg:w-10 group-hover:hidden"
                                />
                                <img
                                  src={`/assets/icons/arrow-simple-down-blue.svg`}
                                  alt="icon"
                                  className="transition rotate-180 w-6 lg:w-10 hidden group-hover:block"
                                />
                              </>
                            ) : (
                              <>
                                <img
                                  src={`/assets/icons/arrow-simple-down.svg`}
                                  alt="icon"
                                  className="transition rotate-0 w-6 lg:w-10 group-hover:hidden"
                                />
                                <img
                                  src={`/assets/icons/arrow-simple-down-blue.svg`}
                                  alt="icon"
                                  className="transition rotate-0 w-6 lg:w-10 hidden group-hover:block"
                                />
                              </>
                            )}
                          </div>
                        </div>
                      )
                    }}
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 -translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 -translate-y-1"
                  >
                    <Popover.Panel className="transform">
                      <div className="col-span-3">
                        <div className="grid grid-cols-1 bg-sec-grey-4">
                          <p className="text-styles pl-10 pr-4 py-6 border-t-2 border-b-2 border-white font-alliance-medium text-blue">
                            {row.content}
                          </p>
                        </div>
                        {/* {row?.content?.map((crow, cidx) => (
                                <div
                                  key={cidx}
                                  className={`grid grid-cols-1 bg-sec-grey-4 ${crow?.className}`}
                                >
                                  <p className="pl-10 pr-4 py-6 border-t-2 border-b-2 border-white font-alliance-medium text-sec-grey-50">
                                    {crow?.content}
                                  </p>
                                  {crow?.subcategories?.map((scrow, scidx) => (
                                    <div
                                      key={scidx}
                                      className={`grid grid-cols-1 pl-10 pr-6 bg-sec-grey-4 ${
                                        scidx < crow?.subcategories?.length - 1
                                          ? 'border-b-2 border-white'
                                          : null
                                      } ${scrow?.className}`}
                                    >
                                      <p className="text-blue">
                                        {scrow?.content}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              ))} */}
                      </div>
                    </Popover.Panel>
                  </Transition>
                </Popover>
              </div>
            </div>
          ))}
      </div>
      {download?.link && (
        <div className="grid grid-cols-1 gap-y-2 pt-6 lg:pt-10">
          <div>
            <a
              href={download?.link ?? '#'}
              download
              target="_blank"
              rel="noreferrer"
            >
              <div className="group grid grid-cols-3 min-w-full bg-blue hover:bg-sec-grey-4 rounded-md">
                <div className="whitespace-nowrap py-0 lg:py-4 pl-4 pr-3 sm:pl-6">
                  <div className="flex items-center">
                    <div className="ml-4 mr-32 py-5">
                      <div className="text-white text-[18px] lg:text-[25px] group-hover:text-blue group-hover:font-alliance-medium">
                        {download.content}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid col-span-2 whitespace-nowrap px-6 py-4 text-sm content-center justify-end">
                  <img
                    src="/assets/icons/download-white.svg"
                    className="mr-4 w-6 lg:w-10 group-hover:hidden"
                  />
                  <img
                    src="/assets/icons/download.svg"
                    className="mr-4 w-6 lg:w-10 hidden group-hover:block"
                  />
                </div>
              </div>
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

export default Info
